import * as React from "react";
import { cn } from "../_internal/cn";
import EyebrowToken from "../EyebrowToken/EyebrowToken";
import NarrativeHeadline from "../NarrativeHeadline/NarrativeHeadline";
import type { TypeNarrativeSummaryProps } from "./NarrativeSummaryTypes";

/**
 * NarrativeSummary — a composed narrative block that pairs a lead column
 * (eyebrow + headline + optional action) with a Summary section and a Key
 * themes list.
 *
 * Renders inner content only — it carries no surface, shadow, or accent of its
 * own. Drop it into a `NarrativeContainer` (see the stories / playground).
 *
 * Layout is CSS Grid driven by container queries, so the columns collapse based
 * on the width the component is actually given, not the viewport:
 * - `"three-column"` → lead | Summary | Key themes.
 * - `"two-column"` → a 1/3 | 2/3 split with Summary and Key themes stacked in
 *   the wider column.
 *
 * Styled via `narrative-summary.css` (Seeds CSS custom properties); consumers
 * import the classes through `@sproutsocial/racine/css/components`. Overrides
 * come via standard `className` / `style`.
 */
const NarrativeSummary = React.forwardRef<
  HTMLDivElement,
  TypeNarrativeSummaryProps
>(
  (
    {
      eyebrow,
      headline,
      headingLevel = 2,
      action,
      summary,
      summaryLabel = "Summary",
      keyThemes,
      keyThemesLabel = "Key themes",
      layout = "three-column",
      className,
      ...props
    },
    ref
  ) => {
    const hasKeyThemes = keyThemes != null && keyThemes.length > 0;

    return (
      <div
        ref={ref}
        className={cn("seeds-narrative-summary", className)}
        {...props}
      >
        <div
          className={cn("seeds-narrative-summary-grid", {
            "seeds-narrative-summary-two-column": layout === "two-column",
          })}
        >
          <div className="seeds-narrative-summary-lead">
            {eyebrow != null && <EyebrowToken>{eyebrow}</EyebrowToken>}
            <div className="seeds-narrative-summary-headline">
              <NarrativeHeadline size="small" headingLevel={headingLevel}>
                {headline}
              </NarrativeHeadline>
            </div>
            {action != null && (
              <div className="seeds-narrative-summary-action">{action}</div>
            )}
          </div>

          <div className="seeds-narrative-summary-content">
            {summary != null && (
              <section className="seeds-narrative-summary-section">
                <p className="seeds-narrative-summary-label">{summaryLabel}</p>
                <p className="seeds-narrative-summary-text">{summary}</p>
              </section>
            )}
            {hasKeyThemes && (
              <section className="seeds-narrative-summary-section">
                <p className="seeds-narrative-summary-label">
                  {keyThemesLabel}
                </p>
                <ul className="seeds-narrative-summary-list">
                  {keyThemes.map((theme, i) => (
                    <li key={i}>{theme}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
    );
  }
);

NarrativeSummary.displayName = "NarrativeSummary";

export default NarrativeSummary;
